<template>
  <section class="chat-panel" :class="{ 'side-panel-open': conversationSidePanel }">
    <header class="chat-header">
      <div>
        <button
          v-if="isGroupConversation"
          type="button"
          class="chat-title-button"
          @click="openGroupOverview"
        >
          <img v-if="activeConversation?.portrait" class="chat-title-avatar image-avatar" :src="activeConversation.portrait" alt="">
          <span v-else class="chat-title-avatar">{{ activeConversation?.avatarText || '?' }}</span>
          <span class="chat-title-copy">
            <h2>{{ activeConversation?.title || language.t('chat.noConversation') }}</h2>
          </span>
        </button>
        <button
          v-else-if="activeConversation"
          type="button"
          class="chat-title-button"
          @click="openSingleProfile"
        >
          <img v-if="singleProfile.portrait" class="chat-title-avatar image-avatar" :src="singleProfile.portrait" alt="">
          <span v-else class="chat-title-avatar">{{ singleProfile.avatarText }}</span>
          <span class="chat-title-copy">
            <h2>{{ activeConversation?.title || language.t('chat.noConversation') }}</h2>
          </span>
        </button>
        <h2 v-else>{{ language.t('chat.noConversation') }}</h2>
        <p>{{ statusText }}</p>
      </div>
      <div class="header-actions chat-header-actions">
        <button v-if="isBotMotherConversation" class="botmother-launch-button" type="button" :title="language.t('botMother.openManager')" @click="botMotherPanelOpen=true"><span aria-hidden="true">B</span><strong>{{ language.t('botMother.open') }}</strong></button>
        <button class="icon-button" type="button" :class="{ active: conversationSidePanel === 'search' }" :title="language.t('common.search')" :aria-expanded="conversationSidePanel === 'search'" @click.stop="openConversationSearch">⌕</button>
        <button class="icon-button" type="button" :class="{ active: conversationSidePanel === 'more' }" :title="language.t('common.more')" :aria-expanded="conversationSidePanel === 'more'" @click.stop="toggleConversationTools">⋯</button>
      </div>
    </header>

    <aside v-if="conversationSidePanel" class="conversation-side-panel" @click.stop>
      <header class="conversation-side-header">
        <button v-if="conversationSidePanel !== 'more'" type="button" class="icon-button" :title="language.t('conversationTools.back')" @click="openConversationTools">‹</button>
        <span v-else class="conversation-side-header-spacer" aria-hidden="true"></span>
        <h3>{{ conversationSideTitle }}</h3>
        <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeConversationSidePanel">×</button>
      </header>

      <section v-if="conversationSidePanel === 'search'" class="conversation-search-panel">
        <form class="conversation-search-form" @submit.prevent="runConversationSearch">
          <input ref="searchInputEl" v-model="conversationSearchQuery" type="search" autocomplete="off" :placeholder="language.t('conversationTools.searchPlaceholder')" @input="queueConversationSearch">
          <button type="submit" :disabled="conversationSearchLoading || !conversationSearchQuery.trim()">{{ language.t('common.search') }}</button>
        </form>
        <div v-if="conversationSearchQuery.trim()" class="conversation-search-summary">
          <span>{{ language.t('conversationTools.searchCount', { count: conversationSearchResults.length }) }}</span>
          <span v-if="conversationSearchLoading">{{ language.t('common.loading') }}</span>
        </div>
        <div v-if="conversationSearchError" class="conversation-side-feedback error" role="alert">
          <span>{{ language.error(conversationSearchError) }}</span>
          <button type="button" :aria-label="language.t('common.close')" @click="conversationSearchError = ''">×</button>
        </div>
        <div v-if="!conversationSearchQuery.trim()" class="conversation-side-empty">
          <strong>{{ language.t('conversationTools.searchPrompt') }}</strong>
          <span>{{ language.t('conversationTools.searchHint') }}</span>
        </div>
        <div v-else-if="!conversationSearchLoading && !conversationSearchResults.length" class="conversation-side-empty">
          <strong>{{ language.t('conversationTools.noSearchResults') }}</strong>
          <span>{{ language.t('conversationTools.tryAnotherKeyword') }}</span>
        </div>
        <div v-else class="conversation-search-results">
          <button
            v-for="message in conversationSearchResults"
            :key="searchMessageKey(message)"
            type="button"
            :class="{ active: focusedSearchMessageId === searchMessageKey(message) }"
            @click="focusSearchMessage(message)"
          >
            <span class="conversation-search-meta"><strong>{{ searchSenderName(message) }}</strong><time>{{ formatConversationToolTime(message.displayTimestamp || message.timestamp) }}</time></span>
            <span class="conversation-search-snippet">{{ searchMessageSummary(message) }}</span>
          </button>
        </div>
      </section>

      <section v-else-if="conversationSidePanel === 'info'" class="conversation-info-panel">
        <div class="conversation-info-summary">
          <img v-if="activeConversation?.portrait" class="conversation-info-avatar image-avatar" :src="activeConversation.portrait" alt="">
          <span v-else class="conversation-info-avatar">{{ activeConversation?.avatarText || '?' }}</span>
          <strong>{{ activeConversation?.title || '-' }}</strong>
          <small>{{ isGroupConversation ? language.t('conversationTools.groupConversation') : language.t('conversationTools.singleConversation') }}</small>
        </div>
        <dl class="conversation-info-fields">
          <div><dt>{{ isGroupConversation ? language.t('groupPanel.id') : language.t('profile.userId') }}</dt><dd>{{ activeConversation?.target || '-' }}</dd></div>
          <template v-if="isGroupConversation">
            <div><dt>{{ language.t('groupPanel.description') }}</dt><dd>{{ groupDetail?.announcement || language.t('groupPanel.notSet') }}</dd></div>
            <div><dt>{{ language.t('groupPanel.ownerAlias') }}</dt><dd>{{ groupDetail?.ownerName || '-' }}</dd></div>
          </template>
          <template v-else>
            <div><dt>{{ language.t('profile.nickname') }}</dt><dd>{{ singleProfile.rawName || '-' }}</dd></div>
            <div><dt>{{ language.t('contactProfile.phone') }}</dt><dd>{{ singleProfile.mobile || language.t('contactProfile.unknown') }}</dd></div>
            <div><dt>{{ language.t('contactProfile.region') }}</dt><dd>{{ singleProfile.address || language.t('contactProfile.unknown') }}</dd></div>
          </template>
        </dl>
        <div v-if="groupBusy" class="conversation-side-empty"><span>{{ language.t('common.loading') }}</span></div>
        <div class="conversation-info-actions">
          <button v-if="isGroupConversation" type="button" @click="openGroupMembers">{{ language.t('chat.viewGroupMembers') }}</button>
          <button v-if="isGroupConversation" type="button" @click="openGroupAlias">{{ language.t('chat.myGroupAlias') }}</button>
          <button v-if="isGroupConversation && canManageGroup" type="button" @click="openGroupManage">{{ language.t('chat.groupManagement') }}</button>
          <button v-if="!isGroupConversation" type="button" @click="openSingleProfile">{{ language.t('conversationTools.editContact') }}</button>
        </div>
      </section>

      <section v-else class="conversation-tools-panel">
        <div class="conversation-info-summary compact">
          <img v-if="activeConversation?.portrait" class="conversation-info-avatar image-avatar" :src="activeConversation.portrait" alt="">
          <span v-else class="conversation-info-avatar">{{ activeConversation?.avatarText || '?' }}</span>
          <strong>{{ activeConversation?.title || '-' }}</strong>
          <small>{{ activeConversation?.target || '-' }}</small>
        </div>
        <DesktopInstall />
        <nav class="conversation-tool-list">
          <button v-if="ownedConversationBot" type="button" @click="openBotSettings"><span aria-hidden="true">⚙</span><strong>{{ language.t('botSettings.title') }}</strong><small>›</small></button>
          <button type="button" @click="openConversationInfo"><span aria-hidden="true">ⓘ</span><strong>{{ isGroupConversation ? language.t('conversationTools.groupInfo') : language.t('conversationTools.contactInfo') }}</strong><small>›</small></button>
          <button type="button" @click="openConversationSearch"><span aria-hidden="true">⌕</span><strong>{{ language.t('common.search') }}</strong><small>›</small></button>
          <button type="button" @click="startConversationSelection"><span aria-hidden="true">☑</span><strong>{{ language.t('conversationTools.selectMessages') }}</strong><small>›</small></button>
          <button type="button" :disabled="conversationToolBusy" @click="toggleConversationMute"><span aria-hidden="true">♩</span><strong>{{ conversationMuted ? language.t('conversationTools.unmute') : language.t('conversationTools.mute') }}</strong><small>{{ conversationMuted ? language.t('conversationTools.on') : language.t('conversationTools.off') }}</small></button>
          <button type="button" :disabled="conversationToolBusy" @click="toggleConversationPinned"><span aria-hidden="true">♡</span><strong>{{ conversationPinned ? language.t('conversationTools.removeSpecial') : language.t('conversationTools.addSpecial') }}</strong><small>{{ conversationPinned ? language.t('conversationTools.on') : language.t('conversationTools.off') }}</small></button>
          <button type="button" :disabled="conversationToolBusy" @click="exportConversation"><span aria-hidden="true">⇩</span><strong>{{ language.t('conversationTools.exportHtml') }}</strong><small>HTML</small></button>
        </nav>
        <form class="conversation-destruct-form" @submit.prevent="saveConversationDestruct">
          <label for="conversation-destruct-select">{{ language.t('conversationTools.disappearingMessages') }}</label>
          <select id="conversation-destruct-select" v-model.number="conversationDestructDraft" :disabled="conversationToolBusy">
            <option :value="0">{{ language.t('conversationTools.destructOff') }}</option>
            <option :value="86400">{{ language.t('conversationTools.destructOneDay') }}</option>
            <option :value="604800">{{ language.t('conversationTools.destructSevenDays') }}</option>
            <option :value="7776000">{{ language.t('conversationTools.destructNinetyDays') }}</option>
          </select>
          <button type="submit" :disabled="conversationToolBusy">{{ language.t('common.save') }}</button>
        </form>
        <nav class="conversation-tool-list danger-list">
          <button type="button" :disabled="conversationToolBusy" @click="closeCurrentConversation"><span aria-hidden="true">⊗</span><strong>{{ language.t('conversationTools.closeChat') }}</strong><small>›</small></button>
          <button type="button" :disabled="conversationToolBusy" @click="clearCurrentConversation"><span aria-hidden="true">⊖</span><strong>{{ language.t('conversationTools.clearChat') }}</strong><small>›</small></button>
          <button v-if="!isGroupConversation" type="button" :disabled="conversationToolBusy" @click="blockCurrentContact"><span aria-hidden="true">⊘</span><strong>{{ language.t('conversationTools.block') }}</strong><small>›</small></button>
          <button type="button" :disabled="conversationToolBusy" @click="deleteCurrentConversation"><span aria-hidden="true">⌫</span><strong>{{ language.t('conversationTools.deleteChat') }}</strong><small>›</small></button>
        </nav>
        <div v-if="conversationToolNotice || conversationToolError" class="conversation-side-feedback" :class="{ error: conversationToolError }" :role="conversationToolError ? 'alert' : 'status'">
          <span>{{ language.error(conversationToolError) || conversationToolNotice }}</span>
          <button type="button" :aria-label="language.t('common.close')" @click="clearConversationToolFeedback">×</button>
        </div>
      </section>
    </aside>

    <div v-if="!activeConversation" class="empty-chat">
      <strong>{{ language.t('chat.chooseConversation') }}</strong>
      <span>{{ language.t('chat.emptyHint') }}</span>
    </div>
    <MessageList
      v-else
      :conversation-id="activeConversation.id"
      :messages="conversations.activeMessages"
      :message-translations="messageTranslations"
      :selection-mode="conversations.selectionMode"
      :selected-message-ids="conversations.selectedMessageIds"
      :favorite-message-ids="conversations.favoriteMessageIds"
      :attention-message-id="conversations.attentionMessageId"
      :focused-message-id="focusedSearchMessageId"
      :can-recall-others="canManageGroup && isGroupConversation"
      :recall-limit-minutes="conversations.activeRecallLimitMinutes"
      :edit-limit-minutes="conversations.activeEditLimitMinutes"
      @message-action="conversations.handleMessageAction"
      @retry-message="conversations.retryMessage"
      @toggle-select="conversations.toggleSelectedMessage"
      @sender-profile-updated="handleSenderProfileUpdated"
    />
    <div v-if="translationError" class="chat-action-feedback error ai-translation-feedback" role="alert">
      <span>{{ language.error(translationError) }}</span>
      <button type="button" class="translation-retry-button" @click="retryMessageTranslations">{{ language.t('translation.retry') }}</button>
      <button type="button" :aria-label="language.t('common.close')" @click="translationError=''">×</button>
    </div>
    <div v-if="conversations.selectionMode" class="selection-toolbar">
      <span>{{ language.t('chat.selectedCount', { count: conversations.selectedMessageIds.length }) }}</span>
      <button type="button" @click="conversations.copySelectedMessages">{{ language.t('chat.copy') }}</button>
      <button type="button" @click="conversations.deleteSelectedMessages">{{ language.t('chat.delete') }}</button>
      <button type="button" @click="conversations.startForwardSelectedMessages">{{ language.t('chat.forward') }}</button>
      <button type="button" @click="conversations.clearSelection">{{ language.t('common.cancel') }}</button>
    </div>
    <div
      v-if="conversations.actionNotice || conversations.actionError"
      class="chat-action-feedback"
      :class="{ error: conversations.actionError }"
      :role="conversations.actionError ? 'alert' : 'status'"
    >
      <span>{{ language.error(conversations.actionError) || conversations.actionNotice }}</span>
      <button type="button" aria-label="关闭提示" @click="conversations.clearActionFeedback">×</button>
    </div>
    <MessageComposer
      :disabled="!activeConversation || !session.isAuthenticated || !conversations.canSendActiveConversation"
      :can-voice-call="canVoiceCall"
      :quote-message="conversations.quotedMessage"
      @cancel-quote="conversations.cancelQuote"
      @send="conversations.sendText"
      @send-attachment="conversations.sendAttachment"
      @send-voice="conversations.sendVoice"
      @send-gift="giftOpen = true"
      @voice-call="startVoiceCall"
      @video-call="startVideoCall"
    />
    <GiftDialog v-if="giftOpen" :conversation="activeConversation" @close="giftOpen = false" />
    <div v-if="conversations.forwardingMessages.length" class="modal-backdrop" @click.self="conversations.cancelForward">
      <section class="forward-dialog">
        <header>
          <h3>{{ language.t('chat.forwardTo') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="conversations.cancelForward">×</button>
        </header>
        <button
          v-for="item in conversations.conversations"
          :key="item.id"
          type="button"
          class="forward-target"
          @click="conversations.completeForward(item.id)"
        >
          <span class="conversation-avatar">{{ item.avatarText }}</span>
          <span>{{ item.title }}</span>
        </button>
      </section>
    </div>
    <div v-if="conversations.editingMessage" class="modal-backdrop" @click.self="conversations.cancelEditMessage">
      <form class="edit-dialog" @submit.prevent="conversations.completeEditMessage">
        <header>
          <h3>{{ conversations.editingMode === 'resend' ? language.t('chat.reEdit') : language.t('chat.editMessage') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="conversations.cancelEditMessage">×</button>
        </header>
        <textarea
          v-model="conversations.editingText"
          maxlength="5000"
          rows="5"
          autofocus
          :aria-invalid="Boolean(conversations.editingError)"
          :disabled="conversations.editingBusy"
          @input="conversations.clearEditFeedback"
        ></textarea>
        <p v-if="conversations.editingError" class="edit-feedback error" role="alert">
          {{ language.error(conversations.editingError) }}
        </p>
        <footer>
          <button type="button" :disabled="conversations.editingBusy" @click="conversations.cancelEditMessage">{{ language.t('common.cancel') }}</button>
          <button type="submit" :disabled="conversations.editingBusy">
            {{ conversations.editingBusy ? '保存中…' : conversations.editingMode === 'resend' ? language.t('common.send') : language.t('common.save') }}
          </button>
        </footer>
      </form>
    </div>
    <div v-if="conversations.messageDetailOpen" class="modal-backdrop" @click.self="conversations.closeMessageDetail">
      <section class="group-dialog message-detail-dialog">
        <header>
          <h3>{{ language.t('chat.messageDetails') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="conversations.closeMessageDetail">×</button>
        </header>
        <div v-if="conversations.messageDetailLoading" class="group-loading">{{ language.t('common.loading') }}</div>
        <template v-else>
          <dl class="group-profile message-detail-meta">
            <div>
              <dt>{{ language.t('chat.sentAt') }}</dt>
              <dd>{{ formatDetailTime(messageDetail?.sentAt) }}</dd>
            </div>
            <div v-if="messageDetail?.editedAt">
              <dt>{{ language.t('chat.editedAt') }}</dt>
              <dd>{{ formatDetailTime(messageDetail.editedAt) }}</dd>
            </div>
            <div>
              <dt>{{ language.t('chat.sender') }}</dt>
              <dd>{{ detailMemberName(messageDetail?.sender) }}</dd>
            </div>
            <template v-if="!isMessageDetailGroup">
              <div>
                <dt>{{ language.t('chat.receiver') }}</dt>
                <dd>{{ detailMemberName(messageDetail?.receiver) }}</dd>
              </div>
              <div>
                <dt>{{ language.t('chat.deliveredAt') }}</dt>
                <dd :class="{ 'message-detail-muted': !messageDetail?.deliveredAt }">
                  {{ messageDetail?.deliveredAt ? formatDetailTime(messageDetail.deliveredAt) : language.t('chat.notDelivered') }}
                </dd>
              </div>
              <div>
                <dt>{{ language.t('chat.readAt') }}</dt>
                <dd :class="{ 'message-detail-muted': !messageDetail?.readAt }">
                  {{ messageDetail?.readAt ? formatDetailTime(messageDetail.readAt) : language.t('chat.unread') }}
                </dd>
              </div>
            </template>
          </dl>
          <span v-if="conversations.messageDetailErrorKey" class="inline-save-status error">{{ language.t(conversations.messageDetailErrorKey) }}</span>
          <template v-if="isMessageDetailGroup">
            <div class="message-detail-summary">
              <span>{{ language.t('chat.read') }} <strong>{{ messageDetailCounts.read || 0 }}</strong></span>
              <span>{{ language.t('chat.unread') }} <strong>{{ messageDetailCounts.unread || 0 }}</strong></span>
              <span>{{ language.t('chat.undelivered') }} <strong>{{ messageDetailCounts.undelivered || 0 }}</strong></span>
              <span>{{ language.t('chat.members') }} <strong>{{ messageDetailCounts.total || 0 }}</strong></span>
            </div>
            <div class="message-detail-groups">
              <section class="message-detail-section">
                <header>
                  <strong>{{ language.t('chat.read') }}</strong>
                  <span>{{ language.t('chat.memberCount', { count: messageDetailCounts.read || 0 }) }}</span>
                </header>
                <div v-if="messageDetailGroup?.read?.length" class="message-detail-member-list">
                  <div v-for="member in messageDetailGroup.read" :key="`read-${member.uid}`" class="message-detail-member-row">
                    <img v-if="member.portrait" class="conversation-avatar image-avatar" :src="member.portrait" alt="">
                    <span v-else class="conversation-avatar">{{ detailMemberAvatar(member) }}</span>
                    <span class="message-detail-member-main">
                      <strong>{{ detailMemberName(member) }}</strong>
                      <small>{{ member.uid }}</small>
                    </span>
                    <span class="message-detail-member-time">{{ formatDetailTime(member.time) }}</span>
                    <span v-if="detailRoleLabel(member.role)" class="message-detail-role">{{ detailRoleLabel(member.role) }}</span>
                  </div>
                </div>
                <div v-else class="contact-picker-empty">{{ language.t('chat.noReadMembers') }}</div>
              </section>

              <section class="message-detail-section">
                <header>
                  <strong>{{ language.t('chat.unread') }}</strong>
                  <span>{{ language.t('chat.memberCount', { count: messageDetailCounts.unread || 0 }) }}</span>
                </header>
                <div v-if="messageDetailGroup?.unread?.length" class="message-detail-member-list">
                  <div v-for="member in messageDetailGroup.unread" :key="`unread-${member.uid}`" class="message-detail-member-row">
                    <img v-if="member.portrait" class="conversation-avatar image-avatar" :src="member.portrait" alt="">
                    <span v-else class="conversation-avatar">{{ detailMemberAvatar(member) }}</span>
                    <span class="message-detail-member-main">
                      <strong>{{ detailMemberName(member) }}</strong>
                      <small>{{ member.uid }}</small>
                    </span>
                    <span class="message-detail-member-time">{{ language.t('chat.deliveredLabel') }} {{ formatDetailTime(member.time) }}</span>
                    <span v-if="detailRoleLabel(member.role)" class="message-detail-role">{{ detailRoleLabel(member.role) }}</span>
                  </div>
                </div>
                <div v-else class="contact-picker-empty">{{ language.t('chat.noUnreadMembers') }}</div>
              </section>

              <section class="message-detail-section">
                <header>
                  <strong>{{ language.t('chat.undelivered') }}</strong>
                  <span>{{ language.t('chat.memberCount', { count: messageDetailCounts.undelivered || 0 }) }}</span>
                </header>
                <div v-if="messageDetailGroup?.undelivered?.length" class="message-detail-member-list">
                  <div v-for="member in messageDetailGroup.undelivered" :key="`undelivered-${member.uid}`" class="message-detail-member-row">
                    <img v-if="member.portrait" class="conversation-avatar image-avatar" :src="member.portrait" alt="">
                    <span v-else class="conversation-avatar">{{ detailMemberAvatar(member) }}</span>
                    <span class="message-detail-member-main">
                      <strong>{{ detailMemberName(member) }}</strong>
                      <small>{{ member.uid }}</small>
                    </span>
                    <span class="message-detail-member-time message-detail-muted">{{ language.t('chat.notDelivered') }}</span>
                    <span v-if="detailRoleLabel(member.role)" class="message-detail-role">{{ detailRoleLabel(member.role) }}</span>
                  </div>
                </div>
                <div v-else class="contact-picker-empty">{{ language.t('chat.noUndeliveredMembers') }}</div>
              </section>
            </div>
          </template>
        </template>
      </section>
    </div>
    <div v-if="singleProfileOpen" class="modal-backdrop" @click.self="closeSingleProfile">
      <section class="group-dialog single-profile-dialog">
        <header>
          <h3>{{ language.t('contactProfile.title') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeSingleProfile">×</button>
        </header>
        <div class="single-profile-summary">
          <img v-if="singleProfile.portrait" class="profile-avatar image-avatar" :src="singleProfile.portrait" alt="">
          <div v-else class="profile-avatar">{{ singleProfile.avatarText }}</div>
          <div class="single-profile-name-row">
            <input v-if="singleAliasEditing" v-model.trim="singleAliasDraft" maxlength="32" autocomplete="off" :placeholder="language.t('contactProfile.remarkPlaceholder')" @keydown.enter.prevent="saveSingleAlias">
            <strong v-else>{{ singleProfile.displayName }}</strong>
            <button v-if="singleAliasEditing" type="button" class="single-profile-edit" :title="language.t('contactProfile.saveRemark')" :disabled="singleProfileSaving" @click="saveSingleAlias">✓</button>
            <button v-else type="button" class="single-profile-edit" :title="language.t('contactProfile.editRemark')" @click="singleAliasEditing = true">✎</button>
          </div>
        </div>
        <dl class="group-profile">
          <div>
            <dt>{{ language.t('profile.userId') }}</dt>
            <dd>{{ singleProfile.uid || '-' }}</dd>
          </div>
          <div>
            <dt>{{ language.t('profile.nickname') }}</dt>
            <dd>{{ singleProfile.rawName || '-' }}</dd>
          </div>
          <div>
            <dt>{{ language.t('contactProfile.phone') }}</dt>
            <dd>{{ singleProfile.mobile || language.t('contactProfile.unknown') }}</dd>
          </div>
          <div>
            <dt>{{ language.t('contactProfile.region') }}</dt>
            <dd>{{ singleProfile.address || language.t('contactProfile.unknown') }}</dd>
          </div>
        </dl>
        <div v-if="singleDeleteOpen" class="single-delete-options">
          <strong>{{ language.t('contactProfile.chooseAction') }}</strong>
          <button type="button" :disabled="singleProfileSaving" @click="removeSingleContact(false, false)">{{ language.t('contactProfile.deleteKeep') }}</button>
          <button type="button" :disabled="singleProfileSaving" @click="removeSingleContact(false, true)">{{ language.t('contactProfile.deleteRemove') }}</button>
          <button type="button" :disabled="singleProfileSaving" @click="removeSingleContact(true, false)">{{ language.t('contactProfile.blockKeep') }}</button>
          <button type="button" :disabled="singleProfileSaving" @click="removeSingleContact(true, true)">{{ language.t('contactProfile.blockRemove') }}</button>
        </div>
        <footer class="single-profile-footer">
          <span v-if="singleProfileNotice" class="inline-save-status">{{ singleProfileNotice }}</span>
          <span v-else-if="singleProfileError" class="inline-save-status error">{{ language.error(singleProfileError) }}</span>
          <button type="button" class="ghost-inline-action" :disabled="singleProfileSaving" @click="singleDeleteOpen ? singleDeleteOpen = false : closeSingleProfile()">{{ singleDeleteOpen ? language.t('contactProfile.back') : language.t('common.cancel') }}</button>
          <button type="button" class="danger-inline-action" :disabled="singleProfileSaving || !singleProfile.uid" @click="singleDeleteOpen = true">
            {{ singleProfileSaving ? language.t('common.processing') : language.t('chat.delete') }}
          </button>
        </footer>
      </section>
    </div>
    <div v-if="groupPanel" class="modal-backdrop">
      <section v-if="groupPanel === 'overview'" class="group-dialog">
        <header>
          <h3>{{ language.t('groupPanel.overview') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeGroupPanel">×</button>
        </header>
        <div v-if="groupBusy" class="group-loading">{{ language.t('common.loading') }}</div>
        <dl v-else class="group-profile">
          <div>
            <dt>{{ language.t('groupPanel.name') }}</dt>
            <dd>{{ groupDetail?.name || activeConversation?.title || '-' }}</dd>
          </div>
          <div>
            <dt>{{ language.t('groupPanel.id') }}</dt>
            <dd>{{ groupDetail?.groupId || activeConversation?.target || '-' }}</dd>
          </div>
          <div>
            <dt>{{ language.t('groupPanel.description') }}</dt>
            <dd>{{ groupDetail?.announcement || language.t('groupPanel.notSet') }}</dd>
          </div>
          <div>
            <dt>{{ language.t('groupPanel.ownerAlias') }}</dt>
            <dd>{{ groupDetail?.ownerName || '-' }}</dd>
          </div>
        </dl>
        <footer>
          <template v-if="isGroupOwner">
            <button type="button" class="danger-inline-action" :disabled="groupBusy" @click="dismissCurrentGroup">{{ language.t('groupPanel.dismiss') }}</button>
            <button type="button" class="primary-inline-action" :disabled="groupBusy" @click="openTransferGroup">{{ language.t('groupPanel.transfer') }}</button>
          </template>
          <button v-else type="button" class="danger-inline-action" :disabled="groupBusy" @click="leaveCurrentGroup">{{ language.t('groupPanel.leave') }}</button>
          <button v-if="canManageGroup" type="button" class="primary-inline-action" :disabled="groupBusy" @click="openGroupManage">{{ language.t('groupPanel.management') }}</button>
        </footer>
      </section>

      <section v-else-if="groupPanel === 'members'" class="group-dialog">
        <header>
          <h3>{{ language.t('groupPanel.members') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeGroupPanel">×</button>
        </header>
        <div v-if="groupBusy" class="group-loading">{{ language.t('common.loading') }}</div>
        <div v-else class="group-member-list">
          <div v-for="member in groupMembers" :key="member.uid" class="group-member-row">
            <img v-if="member.portrait" class="conversation-avatar image-avatar" :src="member.portrait" alt="">
            <span v-else class="conversation-avatar">{{ memberAvatar(member) }}</span>
            <span>
              <strong>{{ member.displayName }}</strong>
              <small>{{ member.role === 'owner' ? language.t('groupPanel.owner') : member.rawDisplayName }}</small>
            </span>
          </div>
          <div v-if="!groupMembers.length" class="contact-picker-empty">{{ language.t('groupPanel.emptyMembers') }}</div>
        </div>
      </section>

      <form v-else-if="groupPanel === 'alias'" class="group-dialog" @submit.prevent="saveGroupAlias">
        <header>
          <h3>{{ language.t('groupPanel.myAlias') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeGroupPanel">×</button>
        </header>
        <label>
          <span>{{ language.t('groupPanel.alias') }}</span>
          <input v-model.trim="groupAliasDraft" maxlength="64" autocomplete="off" :placeholder="language.t('groupPanel.aliasPlaceholder')">
        </label>
        <footer>
          <button type="button" class="ghost-inline-action" @click="closeGroupPanel">{{ language.t('common.cancel') }}</button>
          <button class="primary-inline-action" :disabled="groupBusy">{{ groupBusy ? language.t('common.saving') : language.t('common.save') }}</button>
        </footer>
      </form>

      <section v-else-if="groupPanel === 'transfer'" class="group-dialog">
        <header>
          <h3>{{ language.t('groupPanel.transfer') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeGroupPanel">×</button>
        </header>
        <div v-if="groupBusy" class="group-loading">{{ language.t('common.loading') }}</div>
        <div v-else class="group-member-list">
          <button
            v-for="member in transferableMembers"
            :key="member.uid"
            type="button"
            class="group-member-row selectable"
            :class="{ selected: transferTargetId === member.uid }"
            @click="transferTargetId = member.uid"
          >
            <img v-if="member.portrait" class="conversation-avatar image-avatar" :src="member.portrait" alt="">
            <span v-else class="conversation-avatar">{{ memberAvatar(member) }}</span>
            <span>
              <strong>{{ member.displayName }}</strong>
              <small>{{ member.rawDisplayName }}</small>
            </span>
          </button>
          <div v-if="!transferableMembers.length" class="contact-picker-empty">{{ language.t('groupPanel.transferEmpty') }}</div>
        </div>
        <footer>
          <button type="button" class="ghost-inline-action" @click="closeGroupPanel">{{ language.t('common.cancel') }}</button>
          <button type="button" class="primary-inline-action" :disabled="groupBusy || !transferTargetId" @click="transferCurrentGroup">{{ language.t('groupPanel.confirmTransfer') }}</button>
        </footer>
      </section>

      <section v-else-if="groupPanel === 'manage'" class="group-dialog group-management-dialog">
        <header>
          <h3>{{ language.t('groupPanel.management') }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeGroupPanel">×</button>
        </header>
        <nav class="group-management-tabs">
          <button v-if="isGroupOwner" type="button" :class="{ active: manageSection === 'admins' }" @click="setManageSection('admins')">{{ language.t('groupPanel.adminSettings') }}</button>
          <button type="button" :class="{ active: manageSection === 'profile' }" @click="setManageSection('profile')">{{ language.t('groupPanel.editProfile') }}</button>
          <button type="button" :class="{ active: manageSection === 'kick' }" @click="setManageSection('kick')">{{ language.t('groupPanel.memberManagement') }}</button>
          <button type="button" :class="{ active: manageSection === 'messageActions' }" @click="setManageSection('messageActions')">{{ language.t('groupPanel.messageSettings') }}</button>
          <button type="button" :class="{ active: manageSection === 'mute' }" @click="setManageSection('mute')">{{ language.t('groupPanel.muteSettings') }}</button>
        </nav>

        <div v-if="groupBusy" class="group-loading">{{ language.t('common.loading') }}</div>

        <section v-else-if="manageSection === 'admins'" class="group-manage-section">
          <div class="group-member-list">
            <label
              v-for="member in adminCandidateMembers"
              :key="member.uid"
              class="group-member-row selectable with-check"
            >
              <input type="checkbox" :checked="selectedAdminIds.includes(member.uid)" @change="toggleAdmin(member.uid)">
              <img v-if="member.portrait" class="conversation-avatar image-avatar" :src="member.portrait" alt="">
              <span v-else class="conversation-avatar">{{ memberAvatar(member) }}</span>
              <span>
                <strong>{{ member.displayName }}</strong>
                <small>{{ member.rawDisplayName }}</small>
              </span>
            </label>
            <div v-if="!adminCandidateMembers.length" class="contact-picker-empty">{{ language.t('groupPanel.noAdminCandidates') }}</div>
          </div>
          <footer>
            <button type="button" class="primary-inline-action" @click="saveGroupAdmins">{{ language.t('groupPanel.saveAdmins') }}</button>
          </footer>
        </section>

        <section v-else-if="manageSection === 'profile'" class="group-manage-section">
          <label>
            <span>{{ language.t('groupPanel.groupName') }}</span>
            <input v-model.trim="profileDraft.name" maxlength="128" autocomplete="off">
          </label>
          <label>
            <span>{{ language.t('groupPanel.description') }}</span>
            <textarea v-model.trim="profileDraft.announcement" maxlength="1000" rows="3"></textarea>
          </label>
          <label>
            <span>{{ language.t('groupPanel.welcome') }}</span>
            <textarea v-model.trim="profileDraft.welcomeText" maxlength="1000" rows="3"></textarea>
          </label>
          <footer>
            <button type="button" class="primary-inline-action" @click="saveGroupProfile">{{ language.t('groupPanel.saveProfile') }}</button>
          </footer>
        </section>

        <section v-else-if="manageSection === 'kick'" class="group-manage-section">
          <strong>{{ language.t('groupPanel.addMembers') }}</strong>
          <div class="group-member-list">
            <label v-for="contact in inviteCandidateContacts" :key="contact.uid" class="group-member-row selectable with-check">
              <input type="checkbox" :checked="selectedInviteIds.includes(contact.uid)" @change="toggleInvite(contact.uid)">
              <img v-if="contact.portrait" class="conversation-avatar image-avatar" :src="contact.portrait" alt="">
              <span v-else class="conversation-avatar">{{ String(contact.title || '?').slice(0, 1) }}</span>
              <span><strong>{{ contact.title }}</strong><small>{{ contact.uid }}</small></span>
            </label>
            <div v-if="!inviteCandidateContacts.length" class="contact-picker-empty">{{ language.t('groupPanel.noContacts') }}</div>
          </div>
          <footer><button type="button" class="primary-inline-action" :disabled="!selectedInviteIds.length" @click="inviteSelectedMembers">{{ language.t('groupPanel.addSelected') }}</button></footer>
          <strong>{{ language.t('groupPanel.removeMembers') }}</strong>
          <div class="group-member-list">
            <label
              v-for="member in kickCandidateMembers"
              :key="member.uid"
              class="group-member-row selectable with-check"
            >
              <input type="checkbox" :checked="selectedKickIds.includes(member.uid)" @change="toggleKick(member.uid)">
              <img v-if="member.portrait" class="conversation-avatar image-avatar" :src="member.portrait" alt="">
              <span v-else class="conversation-avatar">{{ memberAvatar(member) }}</span>
              <span>
                <strong>{{ member.displayName }}</strong>
                <small>{{ member.role === 'admin' ? language.t('groupPanel.admin') : member.rawDisplayName }}</small>
              </span>
            </label>
            <div v-if="!kickCandidateMembers.length" class="contact-picker-empty">{{ language.t('groupPanel.noRemoveCandidates') }}</div>
          </div>
          <footer>
            <button type="button" class="danger-inline-action" :disabled="!selectedKickIds.length" @click="kickSelectedMembers">{{ language.t('groupPanel.removeSelected') }}</button>
          </footer>
        </section>

        <section v-else-if="manageSection === 'messageActions'" class="group-manage-section group-message-action-settings">
          <p class="group-setting-hint">{{ language.t('groupPanel.messageTimeHint') }}</p>
          <div class="group-message-action-grid">
            <label>
              <span>{{ language.t('groupPanel.recallMinutes') }}</span>
              <input v-model.number="groupMessageActionDraft.recallLimitMinutes" type="number" min="0" max="525600" step="1" @input="clearGroupMessageActionFeedback">
            </label>
            <label>
              <span>{{ language.t('groupPanel.editMinutes') }}</span>
              <input v-model.number="groupMessageActionDraft.editLimitMinutes" type="number" min="0" max="525600" step="1" @input="clearGroupMessageActionFeedback">
            </label>
          </div>
          <footer class="message-action-save-footer">
            <span v-if="groupMessageActionNotice" class="inline-save-status">{{ groupMessageActionNotice }}</span>
            <span v-else-if="groupMessageActionError" class="inline-save-status error" role="alert">{{ language.error(groupMessageActionError) }}</span>
            <button v-if="hasGroupMessageActionOverride" type="button" class="ghost-inline-action" :disabled="groupBusy" @click="resetGroupMessageActionSettings">{{ language.t('groupPanel.useGlobalMessageSettings') }}</button>
            <button type="button" class="primary-inline-action" :disabled="groupBusy" @click="saveGroupMessageActionSettings">{{ language.t('groupPanel.saveMessageSettings') }}</button>
          </footer>
        </section>

        <section v-else-if="manageSection === 'mute'" class="group-manage-section">
          <div class="single-member-mute">
            <strong>{{ language.t('groupPanel.singleMute') }}</strong>
            <select v-model="singleMuteMemberId">
              <option value="">{{ language.t('groupPanel.selectMember') }}</option>
              <option v-for="member in kickCandidateMembers" :key="member.uid" :value="member.uid">{{ member.displayName }}</option>
            </select>
            <label><span>{{ language.t('groupPanel.muteMinutes') }}</span><input v-model.number="singleMuteMinutes" type="number" min="0" max="525600"></label>
            <button type="button" class="primary-inline-action" :disabled="!singleMuteMemberId || groupBusy" @click="saveSingleMemberMute">{{ language.t('groupPanel.saveSingleMute') }}</button>
          </div>
          <label>
            <span>{{ language.t('groupPanel.muteMode') }}</span>
            <select v-model="muteDraft.mode" @change="clearMuteSaveFeedback">
              <option value="off">{{ language.t('groupPanel.muteOff') }}</option>
              <option value="all_include_admins">{{ language.t('groupPanel.muteAllIncludingAdmins') }}</option>
              <option value="all_except_admins">{{ language.t('groupPanel.muteAllExceptAdmins') }}</option>
            </select>
          </label>
          <label>
            <span>{{ language.t('groupPanel.scheduleType') }}</span>
            <select v-model="muteDraft.scheduleType" @change="clearMuteSaveFeedback">
              <option value="manual">{{ language.t('groupPanel.scheduleManual') }}</option>
              <option value="once">{{ language.t('groupPanel.scheduleOnce') }}</option>
              <option value="daily">{{ language.t('groupPanel.scheduleDaily') }}</option>
            </select>
          </label>
          <div v-if="muteDraft.scheduleType === 'daily'" class="mute-time-grid">
            <label>
              <span>{{ language.t('groupPanel.start') }}</span>
              <input v-model="muteDraft.dailyStart" type="time" @input="clearMuteSaveFeedback">
            </label>
            <label>
              <span>{{ language.t('groupPanel.end') }}</span>
              <input v-model="muteDraft.dailyEnd" type="time" @input="clearMuteSaveFeedback">
            </label>
          </div>
          <footer class="mute-save-footer">
            <span v-if="muteSaveNotice" class="inline-save-status">{{ muteSaveNotice }}</span>
            <span v-else-if="muteSaveError" class="inline-save-status error">{{ language.error(muteSaveError) }}</span>
            <button type="button" class="primary-inline-action" :disabled="groupBusy" @click="saveMuteSetting">{{ language.t('groupPanel.saveMute') }}</button>
          </footer>
        </section>
      </section>
    </div>
    <div v-if="confirmDialog.open" class="modal-backdrop confirm-backdrop" @click.self="resolveConfirmDialog(false)">
      <section class="group-dialog confirm-dialog">
        <header>
          <h3>{{ confirmDialog.title }}</h3>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="resolveConfirmDialog(false)">×</button>
        </header>
        <p>{{ confirmDialog.message }}</p>
        <footer>
          <button type="button" class="ghost-inline-action" @click="resolveConfirmDialog(false)">{{ language.t('common.cancel') }}</button>
          <button
            type="button"
            :class="confirmDialog.danger ? 'danger-inline-action' : 'primary-inline-action'"
            @click="resolveConfirmDialog(true)"
          >
            {{ confirmDialog.confirmText }}
          </button>
        </footer>
      </section>
    </div>
    <BotMotherPanel :open="botMotherPanelOpen" @close="botMotherPanelOpen=false" @identity="handleBotMotherIdentity" />
    <BotSettingsPanel :open="botSettingsPanelOpen" :bot="ownedConversationBot" @close="botSettingsPanelOpen=false" />
  </section>
</template>

<script setup>
import { connectionLabel } from '@/utils/messagePresentation.mjs';
import DesktopInstall from '../DesktopInstall.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import MessageList from './MessageList.vue';
import MessageComposer from './MessageComposer.vue';
import GiftDialog from './GiftDialog.vue';
import BotMotherPanel from './BotMotherPanel.vue';
import BotSettingsPanel from './BotSettingsPanel.vue';
import { imClient } from '@/services/imClient';
import { voiceCallService } from '@/services/voiceCall';
import { MessageTranslationError, nlcTranslationScopeKey, translateMessage, translateVoiceMessage } from '@/services/messageTranslation';
import { downloadConversationHtml } from '@/utils/conversationExport';
import { useConversationStore } from '@/stores/conversations';
import { useContactStore } from '@/stores/contacts';
import { useSessionStore } from '@/stores/session';
import { useLanguageStore } from '@/stores/language';

const conversations = useConversationStore();
const contacts = useContactStore();
const session = useSessionStore();
const language = useLanguageStore();
const messageTranslations = ref({});
const translationError = ref('');
const translationPaused = ref(false);
let translationTimer = 0;
let translationRun = 0;
let translationController = null;
const activeConversation = computed(() => conversations.activeConversation);
const giftOpen = ref(false);
watch(() => activeConversation.value?.id, () => { giftOpen.value = false; });
const conversationSidePanel = ref('');
const searchInputEl = ref(null);
const conversationSearchQuery = ref('');
const conversationSearchResults = ref([]);
const conversationSearchLoading = ref(false);
const conversationSearchError = ref('');
const focusedSearchMessageId = ref('');
const conversationToolBusy = ref(false);
const conversationToolNotice = ref('');
const conversationToolError = ref('');
const conversationDestructDraft = ref(0);
let conversationSearchTimer = 0;
let conversationSearchRequest = 0;
const singleProfileOpen = ref(false);
const singleAliasDraft = ref('');
const singleAliasEditing = ref(false);
const singleDeleteOpen = ref(false);
const singleProfileSaving = ref(false);
const singleProfileNotice = ref('');
const singleProfileError = ref('');
const groupPanel = ref('');
const groupBusy = ref(false);
const groupDetail = ref(null);
const groupMembers = ref([]);
const groupAliasDraft = ref('');
const manageSection = ref('');
const selectedAdminIds = ref([]);
const selectedKickIds = ref([]);
const selectedInviteIds = ref([]);
const singleMuteMemberId = ref('');
const singleMuteMinutes = ref(10);
const transferTargetId = ref('');
const confirmDialog = ref({
  open: false,
  title: '',
  message: '',
  confirmText: '',
  danger: false,
  resolve: null
});
const profileDraft = ref({
  name: '',
  announcement: '',
  welcomeText: ''
});
const muteDraft = ref({
  mode: 'off',
  scheduleType: 'once',
  startsAtLocal: '',
  endsAtLocal: '',
  dailyStart: '09:00',
  dailyEnd: '18:00'
});
const groupMessageActionDraft = ref({
  recallLimitMinutes: 0,
  editLimitMinutes: 0
});
const groupMessageActionNotice = ref('');
const groupMessageActionError = ref('');
const muteSaveNotice = ref('');
const muteSaveError = ref('');
const botMotherPanelOpen = ref(false);
const botMotherUserID = ref('');
const botSettingsPanelOpen = ref(false);
const managedBots = ref([]);
const singlePeerId = computed(() => {
  if (!activeConversation.value || isGroupConversation.value) {
    return '';
  }
  return String(activeConversation.value.target || activeConversation.value.raw?.target || activeConversation.value.id?.replace(/^single-/, '') || '');
});
const singleContact = computed(() => {
  const peerId = singlePeerId.value;
  if (!peerId) {
    return null;
  }
  return contacts.contacts.find(contact => String(contact.uid || contact.id || '') === peerId) || null;
});
const singleProfile = computed(() => {
  const contact = singleContact.value;
  const conversation = activeConversation.value || {};
  const rawUser = contact?.rawUser || {};
  const displayName = contact?.alias
    || rawUser.displayName
    || rawUser.name
    || contact?.title
    || conversation.title
    || singlePeerId.value
    || '未知用户';
  const rawName = rawUser.displayName || rawUser.name || contact?.title || conversation.title || '';

  return {
    uid: singlePeerId.value,
    displayName,
    rawName,
    mobile: rawUser.mobile || conversation.raw?.mobile || '',
    address: rawUser.address || conversation.raw?.address || '',
    portrait: contact?.portrait || conversation.portrait || '',
    avatarText: String(displayName || '?').slice(0, 1).toUpperCase(),
    alias: contact?.alias || ''
  };
});
const isGroupConversation = computed(() => {
  return ['group', 'channel'].includes(activeConversation.value?.type)
    || activeConversation.value?.raw?.type === 'group';
});
const canVoiceCall = computed(() => Boolean(
  session.isAuthenticated
  && activeConversation.value
  && !isGroupConversation.value
  && !isBotMotherConversation.value
  && !['channel', 'system'].includes(activeConversation.value.type)
  && singlePeerId.value
));

function startVoiceCall() {
  if (!canVoiceCall.value) return;
  const peerID = singlePeerId.value;
  voiceCallService.start(peerID, activeConversation.value.title || peerID, activeConversation.value.portrait || singleProfile.value.portrait || '');
}
function startVideoCall() {
  if (!canVoiceCall.value) return;
  const peerID = singlePeerId.value;
  voiceCallService.start(peerID, activeConversation.value.title || peerID, activeConversation.value.portrait || singleProfile.value.portrait || '', 'video');
}
const isBotMotherConversation = computed(() => {
  if (!activeConversation.value || isGroupConversation.value) return false;
  const peerID = String(activeConversation.value.target || activeConversation.value.raw?.target || '').trim();
  const account = String(activeConversation.value.raw?.account || activeConversation.value.raw?.userInfo?.account || '').trim().toLowerCase();
  const title = String(activeConversation.value.title || '').trim().toLowerCase();
  return (botMotherUserID.value !== '' && peerID === botMotherUserID.value)
    || account === 'botmother'
    || title === 'botmother';
});
const ownedConversationBot = computed(() => {
  const peerID = singlePeerId.value;
  if (!peerID) return null;
  return managedBots.value.find(bot => String(bot.botUserID || '') === peerID) || null;
});
const currentGroupRole = computed(() => {
  return groupDetail.value?.myRole || activeConversation.value?.role || activeConversation.value?.raw?.role || '';
});
const isGroupOwner = computed(() => currentGroupRole.value === 'owner');
const canManageGroup = computed(() => ['owner', 'admin'].includes(currentGroupRole.value));
const conversationMuted = computed(() => Number(activeConversation.value?.raw?.recvMsgOpt ?? activeConversation.value?.recvMsgOpt ?? 0) !== 0);
const conversationPinned = computed(() => Boolean(activeConversation.value?.raw?.isPinned ?? activeConversation.value?.isPinned));
const conversationSideTitle = computed(() => {
  if (conversationSidePanel.value === 'search') return language.t('conversationTools.searchMessages');
  if (conversationSidePanel.value === 'info') return isGroupConversation.value ? language.t('conversationTools.groupInfo') : language.t('conversationTools.contactInfo');
  return language.t('conversationTools.title');
});
const hasGroupMessageActionOverride = computed(() => Boolean(groupDetail.value?.messageActionSettings?.configured));
const adminCandidateMembers = computed(() => groupMembers.value.filter(member => member.role !== 'owner'));
const transferableMembers = computed(() => groupMembers.value.filter(member => member.role !== 'owner'));
const inviteCandidateContacts = computed(() => {
  const memberIds = new Set(groupMembers.value.map(member => String(member.uid)));
  return contacts.contacts.filter(contact => !memberIds.has(String(contact.uid || contact.id || '')));
});
const messageDetail = computed(() => conversations.messageDetail || null);
const messageDetailGroup = computed(() => messageDetail.value?.group || null);
const isMessageDetailGroup = computed(() => {
  return messageDetail.value?.conversationType === 'group' || Boolean(messageDetail.value?.group);
});
const messageDetailCounts = computed(() => {
  return messageDetailGroup.value?.counts || {
    read: 0,
    unread: 0,
    undelivered: 0,
    total: 0
  };
});
const kickCandidateMembers = computed(() => {
  return groupMembers.value.filter(member => {
    if (member.uid === session.userId || member.role === 'owner') {
      return false;
    }
    if (currentGroupRole.value === 'admin') {
      return member.role === 'member';
    }
    return currentGroupRole.value === 'owner';
  });
});
const statusText = computed(() => {
  if (activeConversation.value?.type === 'channel' && !conversations.canSendActiveConversation) {
    return language.t('connection.announcementOnly');
  }
  return connectionLabel(session.connectionStatusText,session.connected,language.t);
});

function formatDetailTime(value) {
  const timestamp = Number(value || 0);
  if (!timestamp) {
    return '暂无';
  }
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }).format(timestamp);
}

function detailMemberName(member) {
  return member?.name || member?.displayName || member?.rawName || member?.uid || '-';
}

function detailMemberAvatar(member) {
  return String(detailMemberName(member) || '?').slice(0, 1).toUpperCase();
}

function detailRoleLabel(role) {
  if (role === 'owner') {
    return '群主';
  }
  if (role === 'admin') {
    return '管理员';
  }
  return '';
}

function searchMessageKey(message) {
  return String(message?.messageUid || message?.id || '');
}

function formatConversationToolTime(value) {
  const timestamp = Number(value || 0);
  if (!timestamp) return '-';
  return new Intl.DateTimeFormat(language.current === 'en' ? 'en' : 'zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(timestamp);
}

function searchSenderName(message) {
  if (message?.direction === 'out') return language.t('conversationTools.me');
  return message?.senderName || message?.from || language.t('conversationTools.otherParty');
}

function searchMessageSummary(message) {
  if (message?.recalled || message?.status === 'recalled') return message?.text || language.t('conversationTools.recalledMessage');
  if (message?.text) return message.text;
  if (message?.attachment?.name) return `[${message.type === 'image' ? language.t('conversationTools.image') : language.t('conversationTools.file')}] ${message.attachment.name}`;
  return language.t('conversationTools.unsupportedMessage');
}

function messageMatchesConversationSearch(message, query) {
  const value = String(query || '').trim().toLocaleLowerCase();
  if (!value) return false;
  return [message?.text, message?.originalText, message?.senderName, message?.from, message?.attachment?.name, message?.recallStatus]
    .filter(Boolean)
    .join('\n')
    .toLocaleLowerCase()
    .includes(value);
}

function mergeConversationSearchResults(...lists) {
  const merged = new Map();
  lists.flat().forEach(message => {
    const key = searchMessageKey(message);
    if (!key) return;
    merged.set(key, { ...(merged.get(key) || {}), ...message });
  });
  return Array.from(merged.values())
    .sort((left, right) => Number(right.displayTimestamp || right.timestamp || 0) - Number(left.displayTimestamp || left.timestamp || 0));
}

function localConversationSearch(query) {
  return conversations.activeMessages.filter(message => messageMatchesConversationSearch(message, query));
}

function closeConversationSidePanel() {
  conversationSidePanel.value = '';
  focusedSearchMessageId.value = '';
  conversationSearchError.value = '';
  window.clearTimeout(conversationSearchTimer);
}

function closeGroupMenu() {
  closeConversationSidePanel();
}

function openConversationTools() {
  if (!activeConversation.value) return;
  conversationSidePanel.value = 'more';
  conversationDestructDraft.value = Number(activeConversation.value?.raw?.isMsgDestruct ?? activeConversation.value?.isMsgDestruct)
    ? Number(activeConversation.value?.raw?.msgDestructTime ?? activeConversation.value?.msgDestructTime ?? 0)
    : 0;
}

function toggleConversationTools() {
  if (conversationSidePanel.value === 'more') {
    closeConversationSidePanel();
    return;
  }
  openConversationTools();
}

function openConversationSearch() {
  if (!activeConversation.value) return;
  conversationSidePanel.value = 'search';
  conversationSearchError.value = '';
  nextTick(() => searchInputEl.value?.focus());
  if (conversationSearchQuery.value.trim()) queueConversationSearch();
}

async function openConversationInfo() {
  if (!activeConversation.value) return;
  conversationSidePanel.value = 'info';
  if (isGroupConversation.value) {
    await loadGroupDetail();
    return;
  }
  contacts.refreshContacts({ force: true }).catch(() => {});
}

function queueConversationSearch() {
  window.clearTimeout(conversationSearchTimer);
  const query = conversationSearchQuery.value.trim();
  focusedSearchMessageId.value = '';
  conversationSearchError.value = '';
  conversationSearchResults.value = query ? localConversationSearch(query) : [];
  if (!query) {
    conversationSearchLoading.value = false;
    return;
  }
  conversationSearchTimer = window.setTimeout(runConversationSearch, 260);
}

async function runConversationSearch() {
  window.clearTimeout(conversationSearchTimer);
  const conversation = activeConversation.value;
  const query = conversationSearchQuery.value.trim();
  if (!conversation || !query) return;
  const requestId = ++conversationSearchRequest;
  conversationSearchLoading.value = true;
  conversationSearchError.value = '';
  const localResults = localConversationSearch(query);
  conversationSearchResults.value = localResults;
  try {
    const remoteResults = await imClient.searchConversationMessages(conversation, query, 300);
    if (requestId !== conversationSearchRequest || query !== conversationSearchQuery.value.trim() || conversation.id !== activeConversation.value?.id) return;
    conversationSearchResults.value = mergeConversationSearchResults(localResults, remoteResults)
      .filter(message => messageMatchesConversationSearch(message, query));
  } catch (error) {
    if (requestId !== conversationSearchRequest) return;
    conversationSearchError.value = error?.message || language.t('conversationTools.searchFailed');
  } finally {
    if (requestId === conversationSearchRequest) conversationSearchLoading.value = false;
  }
}

function focusSearchMessage(message) {
  const conversation = activeConversation.value;
  if (!conversation || !message) return;
  conversations.revealMessage(conversation.id, message);
  focusedSearchMessageId.value = searchMessageKey(message);
}

function startConversationSelection() {
  conversations.enterSelectionMode();
  closeConversationSidePanel();
}

function clearConversationToolFeedback() {
  conversationToolNotice.value = '';
  conversationToolError.value = '';
}

async function toggleConversationMute() {
  const conversation = activeConversation.value;
  if (!conversation || conversationToolBusy.value) return;
  conversationToolBusy.value = true;
  clearConversationToolFeedback();
  try {
    const recvMsgOpt = await imClient.setConversationMuted(conversation, !conversationMuted.value);
    conversations.patchConversationSettings(conversation.id, { recvMsgOpt });
    conversationToolNotice.value = recvMsgOpt ? language.t('conversationTools.mutedSaved') : language.t('conversationTools.unmutedSaved');
  } catch (error) {
    conversationToolError.value = error?.message || language.t('conversationTools.muteFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function toggleConversationPinned() {
  const conversation = activeConversation.value;
  if (!conversation || conversationToolBusy.value) return;
  conversationToolBusy.value = true;
  clearConversationToolFeedback();
  try {
    const isPinned = await imClient.setConversationPinned(conversation, !conversationPinned.value);
    conversations.patchConversationSettings(conversation.id, { isPinned });
    conversationToolNotice.value = isPinned ? language.t('conversationTools.specialAdded') : language.t('conversationTools.specialRemoved');
  } catch (error) {
    conversationToolError.value = error?.message || language.t('conversationTools.specialFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function saveConversationDestruct() {
  const conversation = activeConversation.value;
  if (!conversation || conversationToolBusy.value) return;
  conversationToolBusy.value = true;
  clearConversationToolFeedback();
  try {
    const msgDestructTime = await imClient.setConversationDestruct(conversation, conversationDestructDraft.value);
    conversations.patchConversationSettings(conversation.id, { isMsgDestruct: msgDestructTime > 0, msgDestructTime });
    conversationDestructDraft.value = msgDestructTime;
    conversationToolNotice.value = language.t('conversationTools.destructSaved');
  } catch (error) {
    conversationToolError.value = error?.message || language.t('conversationTools.destructFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function exportConversation() {
  const conversation = activeConversation.value;
  if (!conversation || conversationToolBusy.value) return;
  conversationToolBusy.value = true;
  clearConversationToolFeedback();
  conversationToolNotice.value = language.t('conversationTools.exporting');
  try {
    const messages = await imClient.fetchAllConversationMessages(conversation);
    const result = await downloadConversationHtml({
      conversation,
      messages,
      currentUserId: session.userId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    conversationToolNotice.value = language.t('conversationTools.exported', {
      count: result.count,
      attachments: result.embeddedAttachments
    });
  } catch (error) {
    conversationToolError.value = error?.message || language.t('conversationTools.exportFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function closeCurrentConversation() {
  const conversation = activeConversation.value;
  if (!conversation || conversationToolBusy.value) return;
  const confirmed = await requestConfirmDialog({ message: language.t('conversationTools.confirmClose'), confirmText: language.t('conversationTools.closeChat') });
  if (!confirmed) return;
  conversationToolBusy.value = true;
  try {
    await imClient.hideConversation(conversation);
    conversations.removeConversation(conversation.id);
    closeConversationSidePanel();
  } catch (error) {
    conversationToolError.value = error?.message || language.t('conversationTools.closeFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function clearCurrentConversation() {
  const conversation = activeConversation.value;
  if (!conversation || conversationToolBusy.value) return;
  const confirmed = await requestConfirmDialog({ message: language.t('conversationTools.confirmClear'), confirmText: language.t('conversationTools.clearChat'), danger: true });
  if (!confirmed) return;
  conversationToolBusy.value = true;
  clearConversationToolFeedback();
  try {
    await imClient.clearConversationMessages(conversation);
    conversations.clearConversationMessages(conversation.id);
    conversationToolNotice.value = language.t('conversationTools.cleared');
  } catch (error) {
    conversationToolError.value = error?.message || language.t('conversationTools.clearFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function deleteCurrentConversation() {
  const conversation = activeConversation.value;
  if (!conversation || conversationToolBusy.value) return;
  const confirmed = await requestConfirmDialog({ message: language.t('conversationTools.confirmDelete'), confirmText: language.t('conversationTools.deleteChat'), danger: true });
  if (!confirmed) return;
  conversationToolBusy.value = true;
  try {
    await imClient.deleteConversation(conversation);
    conversations.removeConversation(conversation.id);
    closeConversationSidePanel();
  } catch (error) {
    conversationToolError.value = error?.message || language.t('conversationTools.deleteFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function blockCurrentContact() {
  const conversation = activeConversation.value;
  const peerId = singleProfile.value.uid;
  if (!conversation || !peerId || isGroupConversation.value || conversationToolBusy.value) return;
  const confirmed = await requestConfirmDialog({ message: language.t('conversationTools.confirmBlock'), confirmText: language.t('conversationTools.block'), danger: true });
  if (!confirmed) return;
  conversationToolBusy.value = true;
  clearConversationToolFeedback();
  try {
    await imClient.removeContact(peerId, { block: true, deleteConversation: false, conversation });
    await contacts.refreshContacts({ force: true }).catch(() => {});
    conversationToolNotice.value = language.t('conversationTools.blocked');
  } catch (error) {
    conversationToolError.value = error?.message || language.t('contactProfile.blockFailed');
  } finally {
    conversationToolBusy.value = false;
  }
}

async function openSingleProfile() {
  if (!activeConversation.value || isGroupConversation.value) {
    return;
  }
  closeGroupMenu();
  singleProfileNotice.value = '';
  singleProfileError.value = '';
  singleAliasDraft.value = singleProfile.value.alias || '';
  singleAliasEditing.value = false;
  singleDeleteOpen.value = false;
  singleProfileOpen.value = true;
  contacts.refreshContacts().then(() => {
    if (singleProfileOpen.value) {
      singleAliasDraft.value = singleProfile.value.alias || '';
    }
  }).catch(() => {});
}

function closeSingleProfile() {
  if (singleProfileSaving.value) {
    return;
  }
  singleProfileOpen.value = false;
  singleAliasEditing.value = false;
  singleDeleteOpen.value = false;
  singleProfileNotice.value = '';
  singleProfileError.value = '';
}

async function saveSingleAlias() {
  const peerId = singleProfile.value.uid;
  if (!peerId || singleProfileSaving.value) {
    return;
  }
  singleProfileSaving.value = true;
  singleProfileNotice.value = '';
  singleProfileError.value = '';
  try {
    const ok = await contacts.updateContactAlias(peerId, singleAliasDraft.value);
    if (!ok) {
      singleProfileError.value = contacts.actionError || '备注名修改失败';
      return;
    }
    await contacts.refreshContacts({ force: true }).catch(() => {});
    const refreshedContact = contacts.contacts.find(contact => String(contact.uid || contact.id || '') === peerId) || singleContact.value;
    const nextTitle = refreshedContact?.title
      || (singleAliasDraft.value || singleProfile.value.rawName || singleProfile.value.displayName || peerId);
    conversations.upsertConversation({
      ...activeConversation.value,
      title: nextTitle,
      portrait: refreshedContact?.portrait || activeConversation.value?.portrait || '',
      avatarText: String(nextTitle || '?').slice(0, 1).toUpperCase(),
      raw: {
        ...(activeConversation.value?.raw || {}),
        target: peerId,
        mobile: refreshedContact?.rawUser?.mobile || activeConversation.value?.raw?.mobile || '',
        address: refreshedContact?.rawUser?.address || activeConversation.value?.raw?.address || '',
        alias: refreshedContact?.alias || singleAliasDraft.value
      }
    });
    singleAliasDraft.value = refreshedContact?.alias || singleAliasDraft.value;
    singleAliasEditing.value = false;
    singleProfileNotice.value = language.t('contactProfile.remarkSaved');
  } catch (error) {
    singleProfileError.value = error.message || language.t('contactProfile.remarkFailed');
  } finally {
    singleProfileSaving.value = false;
  }
}

async function removeSingleContact(block, deleteConversation) {
  const peerId = singleProfile.value.uid;
  const conversation = activeConversation.value;
  if (!peerId || !conversation || singleProfileSaving.value) return;
  singleProfileSaving.value = true;
  singleProfileNotice.value = '';
  singleProfileError.value = '';
  try {
    await imClient.removeContact(peerId, { block, deleteConversation, conversation });
    await contacts.refreshContacts({ force: true }).catch(() => {});
    if (deleteConversation) conversations.removeConversation(conversation.id);
    singleProfileOpen.value = false;
  } catch (error) {
    singleProfileError.value = error?.message || language.t(block ? 'contactProfile.blockFailed' : 'contactProfile.deleteFailed');
  } finally {
    singleProfileSaving.value = false;
  }
}

function handleSenderProfileUpdated() {
  contacts.refreshContacts({ force: true }).catch(() => {});
  conversations.syncActiveMessages({ allowReadReceipt: true });
  if (groupPanel.value === 'members') {
    loadGroupMembers();
  }
}

function closeGroupPanel() {
  groupPanel.value = '';
  groupBusy.value = false;
  clearMuteSaveFeedback();
  clearGroupMessageActionFeedback();
}

function requestConfirmDialog(options) {
  return new Promise(resolve => {
    confirmDialog.value = {
      open: true,
      title: options.title || language.t('chat.confirmTitle'),
      message: options.message,
      confirmText: options.confirmText || language.t('common.confirm'),
      danger: Boolean(options.danger),
      resolve
    };
  });
}

function resolveConfirmDialog(confirmed) {
  const resolver = confirmDialog.value.resolve;
  confirmDialog.value = {
    open: false,
    title: '',
    message: '',
    confirmText: '',
    danger: false,
    resolve: null
  };
  resolver?.(Boolean(confirmed));
}

async function loadGroupDetail() {
  if (!session.userId || !activeConversation.value?.target) {
    return null;
  }
  groupBusy.value = true;
  try {
    groupDetail.value = await imClient.getGroupDetail(session.userId, activeConversation.value.target);
    conversations.setGroupMessageActionSettings(
      activeConversation.value.target,
      groupDetail.value?.messageActionSettings
    );
    return groupDetail.value;
  } catch (error) {
    conversations.setActionError(error.message || '群资料加载失败');
    return null;
  } finally {
    groupBusy.value = false;
  }
}

async function openGroupOverview() {
  if (!isGroupConversation.value) {
    return;
  }
  closeGroupMenu();
  groupPanel.value = 'overview';
  await loadGroupDetail();
}

async function openGroupMembers() {
  closeGroupMenu();
  groupPanel.value = 'members';
  await loadGroupMembers();
}

async function loadGroupMembers() {
  groupBusy.value = true;
  try {
    groupMembers.value = await imClient.getGroupMembers(session.userId, activeConversation.value.target);
    selectedAdminIds.value = groupMembers.value
      .filter(member => member.role === 'admin')
      .map(member => member.uid);
    return groupMembers.value;
  } catch (error) {
    conversations.setActionError(error.message || '群员加载失败');
    return [];
  } finally {
    groupBusy.value = false;
  }
}

async function openGroupAlias() {
  closeGroupMenu();
  groupPanel.value = 'alias';
  const detail = await loadGroupDetail();
  groupAliasDraft.value = detail?.myAlias || '';
}

function msToLocalInput(value) {
  const timestamp = Number(value || 0);
  if (!timestamp) {
    return '';
  }
  const date = new Date(timestamp);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function localInputToMs(value) {
  return value ? new Date(value).getTime() : 0;
}

function prepareManagementDrafts() {
  clearMuteSaveFeedback();
  clearGroupMessageActionFeedback();
  profileDraft.value = {
    name: groupDetail.value?.name || activeConversation.value?.title || '',
    announcement: groupDetail.value?.announcement || '',
    welcomeText: groupDetail.value?.welcomeText || ''
  };
  const setting = groupDetail.value?.muteSetting || {};
  muteDraft.value = {
    mode: setting.mode || 'off',
    scheduleType: setting.scheduleType || 'once',
    startsAtLocal: msToLocalInput(setting.startsAtMs),
    endsAtLocal: msToLocalInput(setting.endsAtMs),
    dailyStart: setting.dailyStart || '09:00',
    dailyEnd: setting.dailyEnd || '18:00'
  };
  const messageSettings = groupDetail.value?.messageActionSettings || {};
  groupMessageActionDraft.value = {
    recallLimitMinutes: messageSettings.configured
      ? Number(messageSettings.recallLimitMinutes || 0)
      : conversations.activeRecallLimitMinutes,
    editLimitMinutes: messageSettings.configured
      ? Number(messageSettings.editLimitMinutes || 0)
      : conversations.activeEditLimitMinutes
  };
}

function clearMuteSaveFeedback() {
  muteSaveNotice.value = '';
  muteSaveError.value = '';
}

function clearGroupMessageActionFeedback() {
  groupMessageActionNotice.value = '';
  groupMessageActionError.value = '';
}

function setManageSection(section) {
  manageSection.value = section;
  clearMuteSaveFeedback();
  clearGroupMessageActionFeedback();
}

async function openGroupManage() {
  if (!canManageGroup.value) {
    return;
  }
  closeGroupMenu();
  groupPanel.value = 'manage';
  manageSection.value = isGroupOwner.value ? 'admins' : 'profile';
  await loadGroupDetail();
  prepareManagementDrafts();
  await loadGroupMembers();
}

async function openTransferGroup() {
  groupPanel.value = 'transfer';
  transferTargetId.value = '';
  await loadGroupMembers();
}

async function saveGroupAlias() {
  if (!activeConversation.value?.target || groupBusy.value) {
    return;
  }
  groupBusy.value = true;
  try {
    const alias = await imClient.updateGroupAlias(session.userId, activeConversation.value.target, groupAliasDraft.value);
    groupDetail.value = {
      ...(groupDetail.value || {}),
      myAlias: alias
    };
    conversations.setActionNotice('本群昵称已保存');
    closeGroupPanel();
  } catch (error) {
    conversations.setActionError(error.message || '本群昵称保存失败');
  } finally {
    groupBusy.value = false;
  }
}

function toggleAdmin(userId) {
  const selected = new Set(selectedAdminIds.value);
  if (selected.has(userId)) {
    selected.delete(userId);
  } else {
    selected.add(userId);
  }
  selectedAdminIds.value = Array.from(selected);
}

function toggleKick(userId) {
  const selected = new Set(selectedKickIds.value);
  if (selected.has(userId)) {
    selected.delete(userId);
  } else {
    selected.add(userId);
  }
  selectedKickIds.value = Array.from(selected);
}

function toggleInvite(userId) {
  const selected = new Set(selectedInviteIds.value);
  selected.has(userId) ? selected.delete(userId) : selected.add(userId);
  selectedInviteIds.value = Array.from(selected);
}

async function inviteSelectedMembers() {
  if (!selectedInviteIds.value.length || groupBusy.value) return;
  groupBusy.value = true;
  try {
    await imClient.inviteGroupMembers(activeConversation.value.target, selectedInviteIds.value);
    selectedInviteIds.value = [];
    await loadGroupMembers();
    conversations.setActionNotice('群员已添加');
  } catch (error) {
    conversations.setActionError(error.message || '添加群员失败');
  } finally {
    groupBusy.value = false;
  }
}

async function saveSingleMemberMute() {
  if (!singleMuteMemberId.value || groupBusy.value) return;
  groupBusy.value = true;
  try {
    await imClient.muteGroupMember(activeConversation.value.target, singleMuteMemberId.value, Number(singleMuteMinutes.value || 0) * 60);
    conversations.setActionNotice(Number(singleMuteMinutes.value || 0) > 0 ? '群员禁言已设置' : '群员禁言已解除');
    await loadGroupMembers();
  } catch (error) {
    conversations.setActionError(error.message || '群员禁言设置失败');
  } finally {
    groupBusy.value = false;
  }
}

async function saveGroupAdmins() {
  if (!activeConversation.value?.target || groupBusy.value) {
    return;
  }
  groupBusy.value = true;
  try {
    await imClient.setGroupAdmins(session.userId, activeConversation.value.target, selectedAdminIds.value);
    groupMembers.value = groupMembers.value.map(member => {
      if (member.role === 'owner') {
        return member;
      }
      return {
        ...member,
        role: selectedAdminIds.value.includes(member.uid) ? 'admin' : 'member'
      };
    });
    conversations.setActionNotice('管理员已更新');
  } catch (error) {
    conversations.setActionError(error.message || '管理员设置失败');
  } finally {
    groupBusy.value = false;
  }
}

async function saveGroupProfile() {
  if (!activeConversation.value?.target || groupBusy.value) {
    return;
  }
  groupBusy.value = true;
  try {
    const result = await imClient.updateGroupProfile(session.userId, activeConversation.value.target, profileDraft.value);
    groupDetail.value = {
      ...(groupDetail.value || {}),
      name: result.name,
      announcement: result.announcement,
      welcomeText: result.welcomeText
    };
    conversations.upsertConversation({
      ...activeConversation.value,
      title: result.name,
      avatarText: String(result.name || '?').slice(0, 1).toUpperCase(),
      updatedAt: Number(result.updatedAt || Date.now())
    });
    conversations.setActionNotice('群资料已保存');
  } catch (error) {
    conversations.setActionError(error.message || '群资料保存失败');
  } finally {
    groupBusy.value = false;
  }
}

async function saveGroupMessageActionSettings() {
  if (!activeConversation.value?.target || groupBusy.value) {
    return;
  }
  clearGroupMessageActionFeedback();
  const recallLimitMinutes = Number(groupMessageActionDraft.value.recallLimitMinutes);
  const editLimitMinutes = Number(groupMessageActionDraft.value.editLimitMinutes);
  const validLimits = [recallLimitMinutes, editLimitMinutes].every(value => {
    return Number.isInteger(value) && value >= 0 && value <= 525600;
  });
  if (!validLimits) {
    groupMessageActionError.value = language.t('groupPanel.messageTimeInvalid');
    return;
  }
  groupBusy.value = true;
  try {
    const settings = await imClient.updateGroupMessageActionSettings(activeConversation.value.target, {
      recallLimitMinutes,
      editLimitMinutes
    });
    groupDetail.value = {
      ...(groupDetail.value || {}),
      messageActionSettings: settings
    };
    conversations.setGroupMessageActionSettings(activeConversation.value.target, settings);
    groupMessageActionDraft.value = {
      recallLimitMinutes: settings.recallLimitMinutes,
      editLimitMinutes: settings.editLimitMinutes
    };
    groupMessageActionNotice.value = language.t('groupPanel.messageSettingsSaved');
  } catch (error) {
    groupMessageActionError.value = error.message || language.t('groupPanel.messageSettingsFailed');
  } finally {
    groupBusy.value = false;
  }
}

async function resetGroupMessageActionSettings() {
  if (!activeConversation.value?.target || groupBusy.value) {
    return;
  }
  clearGroupMessageActionFeedback();
  groupBusy.value = true;
  try {
    const settings = await imClient.resetGroupMessageActionSettings(activeConversation.value.target);
    groupDetail.value = {
      ...(groupDetail.value || {}),
      messageActionSettings: settings
    };
    conversations.setGroupMessageActionSettings(activeConversation.value.target, settings);
    groupMessageActionDraft.value = {
      recallLimitMinutes: conversations.recallLimitMinutes,
      editLimitMinutes: conversations.editLimitMinutes
    };
    groupMessageActionNotice.value = language.t('groupPanel.globalMessageSettingsRestored');
  } catch (error) {
    groupMessageActionError.value = error.message || language.t('groupPanel.globalMessageSettingsRestoreFailed');
  } finally {
    groupBusy.value = false;
  }
}

async function kickSelectedMembers() {
  if (!selectedKickIds.value.length || groupBusy.value) {
    return;
  }
  const confirmed = await requestConfirmDialog({
    message: language.t('chat.confirmKick'),
    confirmText: language.t('chat.removeFromGroup'),
    danger: true
  });
  if (!confirmed) {
    return;
  }
  groupBusy.value = true;
  try {
    const result = await imClient.kickGroupMembers(session.userId, activeConversation.value.target, selectedKickIds.value);
    const removed = new Set(result.removedIds || selectedKickIds.value);
    groupMembers.value = groupMembers.value.filter(member => !removed.has(member.uid));
    selectedKickIds.value = [];
    conversations.setActionNotice('已移出群组');
  } catch (error) {
    conversations.setActionError(error.message || '踢人失败');
  } finally {
    groupBusy.value = false;
  }
}

async function saveMuteSetting() {
  if (!activeConversation.value?.target || groupBusy.value) {
    return;
  }
  clearMuteSaveFeedback();
  groupBusy.value = true;
  try {
    const setting = await imClient.updateGroupMuteSetting(session.userId, activeConversation.value.target, {
      mode: muteDraft.value.mode,
      scheduleType: muteDraft.value.scheduleType,
      startsAtMs: localInputToMs(muteDraft.value.startsAtLocal),
      endsAtMs: localInputToMs(muteDraft.value.endsAtLocal),
      dailyStart: muteDraft.value.dailyStart,
      dailyEnd: muteDraft.value.dailyEnd
    });
    groupDetail.value = {
      ...(groupDetail.value || {}),
      muteSetting: setting
    };
    muteSaveNotice.value = '禁言设置已保存';
  } catch (error) {
    muteSaveError.value = error.message || '禁言设置保存失败';
  } finally {
    groupBusy.value = false;
  }
}

async function leaveCurrentGroup() {
  if (!activeConversation.value || groupBusy.value) {
    return;
  }
  const confirmed = await requestConfirmDialog({
    message: language.t('chat.confirmLeave'),
    confirmText: language.t('chat.leaveGroup'),
    danger: true
  });
  if (!confirmed) {
    return;
  }
  groupBusy.value = true;
  const ok = await conversations.leaveGroup(activeConversation.value);
  groupBusy.value = false;
  if (ok) {
    closeGroupPanel();
  }
}

async function dismissCurrentGroup() {
  if (!activeConversation.value || groupBusy.value) {
    return;
  }
  const confirmed = await requestConfirmDialog({
    message: language.t('chat.confirmDismiss'),
    confirmText: language.t('chat.dismissGroup'),
    danger: true
  });
  if (!confirmed) {
    return;
  }
  groupBusy.value = true;
  const ok = await conversations.dismissGroup(activeConversation.value);
  groupBusy.value = false;
  if (ok) {
    closeGroupPanel();
  }
}

async function transferCurrentGroup() {
  if (!activeConversation.value?.target || !transferTargetId.value || groupBusy.value) {
    return;
  }
  const confirmed = await requestConfirmDialog({
    message: language.t('chat.confirmTransfer'),
    confirmText: language.t('chat.confirmTransferAction')
  });
  if (!confirmed) {
    return;
  }
  groupBusy.value = true;
  try {
    await imClient.transferGroup(session.userId, activeConversation.value.target, transferTargetId.value);
    groupDetail.value = {
      ...(groupDetail.value || {}),
      myRole: 'member'
    };
    activeConversation.value.role = 'member';
    if (activeConversation.value.raw) {
      activeConversation.value.raw.role = 'member';
    }
    conversations.setActionNotice('群组已转让');
    closeGroupPanel();
    conversations.refreshRemoteConversations();
  } catch (error) {
    conversations.setActionError(error.message || '转让群组失败');
  } finally {
    groupBusy.value = false;
  }
}

function memberAvatar(member) {
  return String(member.displayName || member.uid || '?').slice(0, 1).toUpperCase();
}

function handleBotMotherIdentity(identity) {
  botMotherUserID.value = String(identity?.userID || '').trim();
}

function openBotSettings() {
  if (!ownedConversationBot.value) return;
  closeConversationSidePanel();
  botSettingsPanelOpen.value = true;
}

async function loadBotMotherIdentity() {
  if (!session.isAuthenticated) return;
  try {
    const result = await imClient.listManagedBots();
    handleBotMotherIdentity(result.botMother);
    managedBots.value = Array.isArray(result.bots) ? result.bots : [];
  } catch {
    // The manager remains discoverable by its stable BotMother account/title fallback.
  }
}

function handleNlcPreferenceChanged(event) {
  translationPaused.value = !Boolean(event?.detail?.enabled);
  translationError.value = '';
  translationRun += 1;
  translationController?.abort();
  messageTranslations.value = Object.fromEntries(Object.entries(messageTranslations.value).filter(([, item]) => ['ready', 'same'].includes(item.status)));
  queueMessageTranslations();
}

onMounted(() => {
  loadBotMotherIdentity();
  window.addEventListener('jc-nlc-preference-changed', handleNlcPreferenceChanged);
});

onBeforeUnmount(() => {
  window.removeEventListener('jc-nlc-preference-changed', handleNlcPreferenceChanged);
  window.clearTimeout(conversationSearchTimer);
  window.clearTimeout(translationTimer);
  translationController?.abort();
});

function translationMessageKey(message) {
  return String(message?.messageUid || message?.id || '');
}

function translationSourceText(message) {
  if (message?.type === 'voice') return `voice:${translationMessageKey(message)}:${message?.attachment?.url || ''}`;
  return String(message?.text || '');
}

function isTranslationCandidate(message) {
  return message?.direction === 'in'
    && !message?.recalled
    && message?.status !== 'recalled'
    && (message?.type === 'voice'
      ? Boolean(message?.attachment?.url)
      : message?.type === 'text' && String(message?.text || '').trim() !== '');
}

function translationContext(messages, targetIndex) {
  return messages
    .slice(Math.max(0, targetIndex - 10), targetIndex)
    .filter(item => item?.type === 'text' && !item?.recalled && String(item?.text || '').trim())
    .map(item => ({
      speaker: item.direction === 'out' ? session.displayName || session.userId || 'Me' : item.senderName || item.from || 'User',
      text: String(item.text).trim()
    }));
}

async function runMessageTranslations() {
  if (!session.isAuthenticated || !session.token || !activeConversation.value) return;
  const run = ++translationRun;
  translationController?.abort();
  translationController = new AbortController();
  const messages = conversations.activeMessages || [];
  const candidates = messages
    .map((message, index) => ({ message, index }))
    .filter(({ message }) => isTranslationCandidate(message))
    .slice(-12);
  const targetLanguage = language.current;
  const pending = candidates.filter(({ message }) => {
    const key = translationMessageKey(message);
    const current = messageTranslations.value[key];
    return key && !(current?.sourceText === translationSourceText(message) && current?.targetLanguage === targetLanguage && ['loading', 'ready', 'same', 'skipped'].includes(current?.status));
  });
  if (!pending.length) return;
  const next = { ...messageTranslations.value };
  pending.forEach(({ message }) => {
    next[translationMessageKey(message)] = { status: 'loading', sourceText: translationSourceText(message), targetLanguage };
  });
  messageTranslations.value = next;
  translationError.value = '';
  let cursor = 0;
  const worker = async () => {
    while (cursor < pending.length && run === translationRun) {
      const item = pending[cursor++];
      const key = translationMessageKey(item.message);
      try {
        const request = {
          token: session.token,
          cachedOnly: translationPaused.value,
          targetLanguage,
          context: translationContext(messages, item.index),
          signal: translationController.signal
        };
        let result;
        if (item.message.type === 'voice') {
          const audioResponse = await fetch(item.message.attachment.url, { signal: translationController.signal });
          if (!audioResponse.ok) throw new Error('无法读取语音文件');
          const audio = await audioResponse.blob();
          const mime = audio.type || item.message.attachment.mime || 'audio/webm';
          const extension = mime.includes('ogg') ? 'ogg' : mime.includes('mp4') || mime.includes('m4a') ? 'm4a' : mime.includes('mpeg') || mime.includes('mp3') ? 'mp3' : mime.includes('wav') ? 'wav' : 'webm';
          result = await translateVoiceMessage({
            ...request,
            messageId: key,
            sourceLanguage: item.message.attachment.sourceLanguage || '',
            audio,
            filename: `voice-${key}.${extension}`
          });
        } else {
          result = await translateMessage({
            ...request,
            message: { id: key, text: item.message.text, sender: item.message.senderName || item.message.from || '' }
          });
        }
        if (run !== translationRun) return;
        if (!result) {
          messageTranslations.value = { ...messageTranslations.value, [key]: { status: 'skipped', sourceText: translationSourceText(item.message), targetLanguage } };
          continue;
        }
        messageTranslations.value = {
          ...messageTranslations.value,
          [key]: {
            status: result.same_language && item.message.type !== 'voice' ? 'same' : 'ready',
            text: result.same_language && item.message.type === 'voice'
              ? result.transcribed_text || result.translated_text || ''
              : result.translated_text || '',
            transcript: result.transcribed_text || '',
            transcriptionOnly: Boolean(result.transcription_only),
            sourceLanguage: result.source_language || '',
            sourceText: translationSourceText(item.message),
            targetLanguage
          }
        };
      } catch (error) {
        if (error?.name === 'AbortError' || run !== translationRun) return;
        messageTranslations.value = {
          ...messageTranslations.value,
          [key]: { status: 'failed', sourceText: translationSourceText(item.message), targetLanguage }
        };
        if (error instanceof MessageTranslationError && ['TRANSLATION_DISABLED', 'TRANSLATION_NOT_CONFIGURED', 'NLC_USER_DISABLED'].includes(error.code)) {
          translationPaused.value = true;
          translationController?.abort();
          messageTranslations.value = Object.fromEntries(Object.entries(messageTranslations.value).filter(([, item]) => ['ready', 'same'].includes(item.status)));
          translationError.value = '';
          return;
        }
        if (error instanceof MessageTranslationError && error.code === 'NLC_SUBSCRIPTION_REQUIRED') {
          translationPaused.value = true;
          translationController?.abort();
          translationError.value = language.t('translation.subscriptionRequired');
          return;
        }
        translationError.value = error?.message || language.t('translation.failed');
      }
    }
  };
  await Promise.all([worker(), worker()]);
}

function queueMessageTranslations() {
  window.clearTimeout(translationTimer);
  translationTimer = window.setTimeout(runMessageTranslations, 180);
}

function retryMessageTranslations() {
  translationError.value = '';
  queueMessageTranslations();
}

watch(
  () => session.token,
  (nextToken, previousToken) => {
    if (nextToken !== previousToken) translationPaused.value = false;
  }
);

watch(
  () => nlcTranslationScopeKey(activeConversation.value?.id, language.current, session.token),
  () => {
    translationRun += 1;
    translationController?.abort();
    messageTranslations.value = {};
    translationError.value = '';
    queueMessageTranslations();
  },
  { immediate: true }
);

watch(
  () => (conversations.activeMessages || []).map(message => `${translationMessageKey(message)}:${message.status || ''}:${message.recalled ? '1' : '0'}:${message.text || ''}`).join('|'),
  queueMessageTranslations
);

watch(
  () => activeConversation.value?.id,
  () => {
    closeGroupMenu();
    closeSingleProfile();
    closeGroupPanel();
    conversationSearchQuery.value = '';
    conversationSearchResults.value = [];
    conversationSearchLoading.value = false;
    conversationSearchError.value = '';
    focusedSearchMessageId.value = '';
    clearConversationToolFeedback();
    groupDetail.value = null;
    groupMembers.value = [];
    groupAliasDraft.value = '';
    selectedAdminIds.value = [];
    selectedKickIds.value = [];
    transferTargetId.value = '';
    manageSection.value = '';
    botMotherPanelOpen.value = false;
    botSettingsPanelOpen.value = false;
    if (session.isAuthenticated) loadBotMotherIdentity();
  }
);
</script>
